// @oasis-permission-inherits: /videos (mirage-videos:view)
import { ApiUnavailableError, KnownApiError } from "@mirage-web/networking/known-api-error";
import { useQueries } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Clock, ListChecks, Loader2, Play, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import {
  MIN_PANELS_FOR_FEEDBACK,
  buildDpoEvalImportRequest,
  hasDpoPanelCountMismatch,
  isDpoEvalTestId,
} from "~/modules/dpo-feedback/manifest";
import { listDpoPanelBatchesQuery } from "~/modules/dpo-feedback/queries";
import type { DpoPanelBatch } from "~/modules/dpo-feedback/schemas";
import {
  collectDpoTestedClipIds,
  diffDpoBatchPanels,
  isDpoTestForBatch,
  type DpoBatchTranche,
} from "~/modules/dpo-feedback/tranches";
import { MirageEvalStatusBadge } from "~/modules/mirage-video-evals/components";
import { formatMirageEvalDate } from "~/modules/mirage-video-evals/formatters";
import {
  importMirageVideoEvalTestMutation,
  openMirageVideoEvalTestMutation,
} from "~/modules/mirage-video-evals/mutations";
import {
  getMirageVideoEvalTestQuery,
  listMirageVideoEvalTestsQuery,
} from "~/modules/mirage-video-evals/queries";

export const Route = createFileRoute("/_dash/videos/evals/dpo/")({
  component: RouteComponent,
});

function batchKey(batch: DpoPanelBatch): string {
  return `${batch.modelFamily}/${batch.checkpointSlug}`;
}

function isEndpointMissing(error: Error | null): boolean {
  return (
    (error instanceof KnownApiError && error.responseStatus === 404) ||
    (error instanceof ApiUnavailableError && error.responseStatus === 404)
  );
}

type BatchTrancheState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; tranche: DpoBatchTranche };

function openFeedbackHint({
  newPanelCount,
  trancheState,
}: {
  newPanelCount: number;
  trancheState: BatchTrancheState;
}): string | undefined {
  if (trancheState.kind === "loading") {
    return "Checking which panels are already in tests";
  }
  if (trancheState.kind === "error") {
    return "Cannot open feedback: failed to load existing tests";
  }
  if (newPanelCount === 0) {
    return "All panels from this batch are already in tests";
  }
  if (newPanelCount < MIN_PANELS_FOR_FEEDBACK) {
    return `Needs at least ${MIN_PANELS_FOR_FEEDBACK} new panels to open feedback`;
  }
  return;
}

function RouteComponent() {
  const navigate = useNavigate();
  const [openingBatchKey, setOpeningBatchKey] = useState<string | null>(null);

  const batchesQuery = listDpoPanelBatchesQuery.useQuery({ retry: false });
  const testsQuery = listMirageVideoEvalTestsQuery.useQuery({ params: { limit: 50 } });

  const batches = batchesQuery.data?.batches ?? [];
  const dpoTests = (testsQuery.data?.tests ?? []).filter((test) => isDpoEvalTestId(test.testId));
  const panelFeedPending = isEndpointMissing(batchesQuery.error);

  const dpoTestDetailQueries = useQueries({
    queries: dpoTests.map((test) =>
      getMirageVideoEvalTestQuery.queryOptions({ pathParams: { testId: test.testId } })
    ),
  });

  const trancheStateFor = (batch: DpoPanelBatch): BatchTrancheState => {
    if (testsQuery.error) {
      return { kind: "error" };
    }
    if (testsQuery.isLoading) {
      return { kind: "loading" };
    }
    const matchingDetails = dpoTests.flatMap((test, index) =>
      isDpoTestForBatch(test, batch) ? [dpoTestDetailQueries[index]] : []
    );
    if (matchingDetails.some((query) => query.isError)) {
      return { kind: "error" };
    }
    const details = matchingDetails.map((query) => query.data);
    if (details.some((detail) => !detail)) {
      return { kind: "loading" };
    }
    const testedClipIds = collectDpoTestedClipIds(
      details.filter((detail) => typeof detail !== "undefined")
    );
    return { kind: "ready", tranche: diffDpoBatchPanels(batch, testedClipIds) };
  };

  const openFeedback = async (batch: DpoPanelBatch, tranche: DpoBatchTranche) => {
    const key = batchKey(batch);
    setOpeningBatchKey(key);
    try {
      const request = buildDpoEvalImportRequest({
        batch: { ...batch, panels: tranche.newPanels },
      });
      await importMirageVideoEvalTestMutation.trigger({ params: request });
      await openMirageVideoEvalTestMutation.trigger({
        pathParams: { testId: request.testId },
        params: {},
      });
      toast.success("DPO feedback test opened", { description: request.testId });
      await navigate({
        to: "/videos/evals/dpo/$testId",
        params: { testId: request.testId },
      });
    } catch (error) {
      toast.error("Failed to open feedback", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setOpeningBatchKey(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2">
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to="/videos/evals">Back to evals</Link>
            </Button>
          </div>
          <h1 className="text-xl font-bold">DPO Feedback</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Rate 9-seed re-roll panels per checkpoint. Each Open creates a test from the panels not
            yet in any DPO test; feedback opens once a batch has at least {MIN_PANELS_FOR_FEEDBACK}{" "}
            new panels.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => batchesQuery.refetch()}
          disabled={batchesQuery.isFetching}
        >
          <RefreshCw className={cn("size-4", batchesQuery.isFetching && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Clock className="size-4" />
          Panel batches
        </h2>

        {batchesQuery.isLoading && (
          <div className="rounded-md border p-5 text-sm text-muted-foreground">
            Loading panel batches...
          </div>
        )}

        {panelFeedPending && (
          <div className="rounded-md border border-dashed p-5 text-sm text-muted-foreground">
            The panel-batch feed is not available yet (the captions-server endpoint is pending
            deploy). Existing DPO tests below still work.
          </div>
        )}

        {batchesQuery.error && !panelFeedPending && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            <p className="font-medium">Failed to load panel batches</p>
            <pre className="mt-2 whitespace-pre-wrap text-xs">{batchesQuery.error.message}</pre>
          </div>
        )}

        {!batchesQuery.isLoading && !batchesQuery.error && batches.length === 0 && (
          <div className="rounded-md border p-5 text-sm text-muted-foreground">
            No panel batches yet. Flagged segments appear here once the miner re-rolls them.
          </div>
        )}

        {batches.length > 0 && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Model family</TableHead>
                  <TableHead>Checkpoint</TableHead>
                  <TableHead>Panels</TableHead>
                  <TableHead>Last updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => {
                  const key = batchKey(batch);
                  const trancheState = trancheStateFor(batch);
                  const tranche = trancheState.kind === "ready" ? trancheState.tranche : null;
                  const newPanelCount = tranche?.newPanels.length ?? 0;
                  const ready = tranche !== null && newPanelCount >= MIN_PANELS_FOR_FEEDBACK;
                  const isOpening = openingBatchKey === key;
                  return (
                    <TableRow key={key}>
                      <TableCell className="font-mono text-xs">{batch.modelFamily}</TableCell>
                      <TableCell className="font-mono text-xs">{batch.checkpointSlug}</TableCell>
                      <TableCell>
                        <span className={cn(!ready && "text-muted-foreground")}>
                          {batch.panelCount}
                        </span>
                        {tranche && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {tranche.testedPanelCount} in tests |{" "}
                            <span
                              className={cn(newPanelCount > 0 && "font-medium text-foreground")}
                            >
                              {newPanelCount} new
                            </span>
                          </div>
                        )}
                        {trancheState.kind === "loading" && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            checking existing tests...
                          </div>
                        )}
                        {trancheState.kind === "error" && (
                          <div className="mt-1 text-xs text-destructive">
                            failed to load existing tests
                          </div>
                        )}
                        {hasDpoPanelCountMismatch(batch) && (
                          <div className="mt-1 text-xs text-amber-600">
                            showing {batch.panels.length} of {batch.panelCount} panels
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{formatMirageEvalDate(batch.lastUpdated)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="xs"
                          onClick={() => tranche && openFeedback(batch, tranche)}
                          disabled={!ready || openingBatchKey !== null}
                          title={openFeedbackHint({ newPanelCount, trancheState })}
                        >
                          {isOpening ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Play className="size-4" />
                          )}
                          Open feedback{tranche ? ` (${newPanelCount} new panels)` : ""}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <ListChecks className="size-4" />
          DPO feedback tests
        </h2>

        {testsQuery.isLoading && (
          <div className="rounded-md border p-5 text-sm text-muted-foreground">
            Loading tests...
          </div>
        )}

        {testsQuery.error && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            <p className="font-medium">Failed to load eval tests</p>
            <pre className="mt-2 whitespace-pre-wrap text-xs">{testsQuery.error.message}</pre>
          </div>
        )}

        {!testsQuery.isLoading && !testsQuery.error && dpoTests.length === 0 && (
          <div className="rounded-md border p-5 text-sm text-muted-foreground">
            No DPO feedback tests yet.
          </div>
        )}

        {dpoTests.length > 0 && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Panels</TableHead>
                  <TableHead>Votes</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dpoTests.map((test) => (
                  <TableRow key={test.testId}>
                    <TableCell>
                      <div className="min-w-0">
                        <Link
                          to="/videos/evals/dpo/$testId"
                          params={{ testId: test.testId }}
                          className="font-medium text-primary hover:underline"
                        >
                          {test.name}
                        </Link>
                        <div className="mt-1 font-mono text-xs text-muted-foreground">
                          {test.testId}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <MirageEvalStatusBadge status={test.status} />
                    </TableCell>
                    <TableCell>{test.clipCount}</TableCell>
                    <TableCell>{test.voteCount}</TableCell>
                    <TableCell>{formatMirageEvalDate(test.updatedAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button asChild variant="outline" size="xs">
                          <Link to="/videos/evals/dpo/$testId" params={{ testId: test.testId }}>
                            Rate
                          </Link>
                        </Button>
                        <Button asChild variant="outline" size="xs">
                          <Link to="/videos/evals/$testId" params={{ testId: test.testId }}>
                            Details
                          </Link>
                        </Button>
                        <Button asChild variant="outline" size="xs">
                          <Link to="/videos/evals/$testId/results" params={{ testId: test.testId }}>
                            Results
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
